import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";

type DeleteRenewalDialogProps = {
  renewalId: number;
  licenseId: number;
  renewalDate: Date;
  isOpen: boolean;
  onClose: () => void;
};

export default function DeleteRenewalDialog({
  renewalId,
  licenseId,
  renewalDate,
  isOpen,
  onClose,
}: DeleteRenewalDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { mutate: deleteRenewal, isPending } = useMutation({
    mutationFn: async () => {
      return await apiRequest(`/api/historical-renewals/${renewalId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'historical-renewals'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      toast({
        title: "Renewal deleted",
        description: "The renewal record has been deleted successfully.",
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Failed to delete renewal",
        description: "There was an error deleting the renewal record. Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={() => !isPending && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center text-red-600">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Confirm Deletion
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this renewal record from {formatDate(renewalDate)}?
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-slate-50 border rounded-md p-4">
            <p className="text-sm text-slate-600">
              This action cannot be undone. This will permanently delete the renewal record and 
              any attached invoice documents.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button 
            variant="destructive"
            disabled={isPending}
            onClick={() => {
              deleteRenewal();
            }}
          >
            {isPending ? 'Deleting...' : 'Delete Renewal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}